<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading || !isReadyToScan">
    <div class="step-label">
      {{ $t('pair_with_keyence_handyterminal') }}
    </div>
    <el-button id="Home_OnClickBarcodeScan" type="primary" class="full-button" @click="startScan">
      <div class="action-button">
        <IconScanQR />
        {{ $t('Scan barcode with keyence HT') }}
      </div>
    </el-button>
    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodes.length }}
      </span>
    </div>
    <div v-if="estimateChildCount > 0" class="barcode-number-block">
      <span>
        {{ $t('Number of reserved children barcode') }}
      </span>
      <span class="label-box">
        {{ estimateChildCount }}
      </span>
    </div>
    <ScannerConfig :scannerConfigName="'PairingScan'" :shouldBulkMode="true" />
    <el-button
      circle
      :disabled="scannedBarcodes.length === 0"
      type="primary"
      class="submit-button"
      @click="forward"
    >
      {{ $t('OK') }}
    </el-button>
  </div>
  <el-dialog
    :lock-scroll="true"
    center
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    custom-class="pair-scan-scanned-list-modal"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodes.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodes || []" />
    </template>
    <template #footer>
      <div class="pair-scan-scanned-list-dialog-footer">
        <el-button type="primary" class="full-w-button pair-scan-scanned-list-ok-button" @click="onCloseDialog">
          <span class="pair-scan-scanned-list-ok-label">{{ $t('OK') }}</span>
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="onCloseDialog"
            :visible="dialogVisible"
            :newLayer="true"
          />
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import { SAVE_SCANNED_BARCODES, PAIRING, CLEAR_ERROR } from '@/store/actions'
import { getBarcode, getChildren, getEstimateBarcodeChildren, pairBarcode } from '@/utils/api'
import errorHandler from '@/utils/errorHandler'
import { PROJECT_CODE } from '@/utils/constants'
import {
  CodeTypeSetting,
  ConfigId,
  defaultCodeTypeSetting,
  isKeyenceEnterpriseBrowser,
  KeyenceModule,
  ScanResult,
} from '@/utils/keyenceHT/keyenceModules'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import {
  getBarcodeIdFromQrCode,
  isQrCode,
  openMessage,
  shouldHandleAsNewBarcode,
} from '@/utils/helpers'
import IconScanQR from '@/components/svg/IconScanQR.vue'
import ScannerConfig from '@/components/keyenceHT/ScannerConfig.vue'
import { isEmpty } from '@/utils/typeChecker'
import { IAPIErrorCode, TError } from '@/utils/types/commonTypes'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType } from '@/utils/types/fieldDefinition'
@Options({
  components: {
    IconScanQR,
    ScannerConfig: ScannerConfig,
    BarcodeLabelList,
    KeyenceHTHardKeyButton,
  },
  name: 'Pairscan',
})
export default class PairScan extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  prevScannedText = ''
  scannedBarcodeId = ''
  scannedBarcodes: IBarcode[] = []
  barcodes = [] as IBarcode[]
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  children = [] as IBarcode[]
  estimateChildCount = 0
  extParams = {} as Record<string, unknown>
  isReadyToScan = true
  backupCodeTypeSetting: CodeTypeSetting = defaultCodeTypeSetting

  async created() {
    try {
      this.loading = true
      this.children = await getChildren(this.$route.params.barcodeId)
      const { count } = await getEstimateBarcodeChildren(this.$route.params.barcodeId)
      this.estimateChildCount = count
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  async mounted() {
    if (isKeyenceEnterpriseBrowser() && KeyenceModule && KeyenceModule.Scanner) {
      this.backupCodeTypeSetting = KeyenceModule.Scanner.getConfig(ConfigId.CodeType) as CodeTypeSetting
      KeyenceModule.Scanner.setConfig(ConfigId.CodeType, defaultCodeTypeSetting)

      KeyenceModule.Scanner.clearReadCallback()
      KeyenceModule.Scanner.setReadCallback('keyenceHandyTerminalScanResultCallback')
    }

    window.keyenceHandyTerminalScanResultCallback = async (result: ScanResult) => {
      if (result.mDecodeResult !== 'SUCCESS') {
        return
      }
      const resultString =
        typeof result.mStringData === 'string' ? result.mStringData : result.mStringData[0].toString()
      const scannedText = resultString.trim()
      await this.handleScannedText(scannedText, PROJECT_CODE)
    }
  }

  acknowledgeProcessedScan(scannedText: string) {
    this.prevScannedText = scannedText
  }

  clearPreviousScan() {
    this.prevScannedText = ''
  }

  @Watch('$store.state.dialogVisible')
  onErrorDialogVisibilityChanged(isVisible: boolean) {
    if (!isVisible) {
      this.clearPreviousScan()
    }
  }

  async startScan() {
    KeyenceModule.Scanner.startRead()
  }

  openErrorToast(message: string) {
    openMessage(message, 'error', 0)
  }

  get barcodeName(): string {
    return this.barcodeType?.name || ''
  }

  get currentTrackPointName() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name
  }

  errorVariableTemplate() {
    return {
      barcodeType: this.barcodeName,
      fTP1: this.currentTrackPointName,
      tTP2: this.nextSelectTrackingPointName,
    }
  }

  get currentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  get prevBarcodeId(): string {
    return this.currentBarcodeId
  }

  async handleScannedText(plainText: string, projectCode: string) {
    if (!shouldHandleAsNewBarcode(plainText, this.prevScannedText, this.prevBarcodeId)) {
      return
    }
    const shouldTreatAsQrCode = isQrCode(plainText)
    const barcodeId = shouldTreatAsQrCode ? getBarcodeIdFromQrCode(plainText) : `projectCode=${projectCode}`
    const externalId = shouldTreatAsQrCode ? undefined : plainText
    if (!barcodeId) {
      return
    }

    try {
      const scannedBarcode: IBarcode = await getBarcode(barcodeId, externalId)
      if (this.scannedBarcodes.find((barcode) => barcode.id === scannedBarcode?.id)) {
        return
      }

      this.validateBarcodeForPairing(scannedBarcode)
      await this.doPairingBarcode(scannedBarcode.id, this.currentBarcodeId)

      this.scannedBarcodeId = scannedBarcode.id
      const isAlreadyAdded =
        this.barcode?.id === scannedBarcode.id ||
        this.scannedBarcodes.find((barcode) => barcode.id === scannedBarcode.id)
      if (!isAlreadyAdded) {
        this.scannedBarcodes.push(scannedBarcode)
      }

      openMessage(this.$t('barcode detected'), 'success')
      this.acknowledgeProcessedScan(plainText)
    } catch (error) {
      this.currentPlainText = ''
      if (error === 'cancel') {
        return
      }

      if (typeof error === 'object' && error?.hasOwnProperty('id')) {
        const message = this.$t(`errors.${(error as IAPIErrorCode)?.id?.pop()}`)
        this.openErrorToast(message)
        return
      }
      if (typeof error === 'string') {
        const modelObject = this.errorVariableTemplate()
        errorHandler(error as TError, modelObject)
        return
      }
      if (!isEmpty(error)) {
        const message = JSON.stringify(error)
        this.openErrorToast(message)
      }
    }
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): Record<string, IBarcodeDefinitionType> {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  get isAllowForcePairing() {
    return this.$store.state.project?.details?.allowForcePairing || false
  }

  isBarcodeInChildrenList(barcode: IBarcode): boolean {
    return !!this.children.find((bc) => bc.id === barcode.id)
  }

  validateBarcodeForPairing(scannedBarcode: IBarcode) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = scannedBarcode.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType, hasParent } = scannedBarcode

    if (this.currentBarcodeId === scannedBarcode?.id) {
      throw this.$t('barcode_pairing_by_itself_error')
    }

    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }

    if (this.isBarcodeInChildrenList(scannedBarcode)) {
      throw this.$t('scanned_barcode_in_children_list')
    }

    if (hasParent && !this.isAllowForcePairing) {
      throw this.$t('barcode_already_paired')
    }

    const barcodeTypeItem = this.barcodeTypeInfo(barcodeType)
    if (!barcodeTypeItem?.allowToBePaired || scannedBarcode.isDeactivated || !scannedBarcode.isReadyToAddTrackData) {
      throw this.$t('barcode_can_not_be_paired')
    }
    if (
      scannedBarcode?.reservedParentIds &&
      scannedBarcode.reservedParentIds.length > 0 &&
      !scannedBarcode.reservedParentIds.includes(this.currentBarcodeId)
    ) {
      throw this.$t('errors.3046')
    }
  }

  async doPairingBarcode(barcodeId: string, currentBarcodeId: string) {
    const pairBarcodeRequest = {
      parentBarcodeId: currentBarcodeId,
      childrenBarcodeIds: [barcodeId],
      isDryRun: true,
    }
    try {
      await pairBarcode(pairBarcodeRequest)
    } catch (error) {
      if (error === '3044' && this.isAllowForcePairing) {
        await this.$confirm('', this.$t('confirm_overwrite_parent'), {
          confirmButtonText: this.$t('ok'),
          confirmButtonClass: 'danger',
          cancelButtonText: this.$t('cancel'),
        })
          .then(async () => {
            const payload = {
              ...pairBarcodeRequest,
              forcePairing: true,
            }
            await pairBarcode(payload)
            this.extParams = {
              ...this.extParams,
              forcePairing: true,
            }
          })
          .catch((err: unknown) => {
            throw err
          })
          .finally(() => this.$store.commit(CLEAR_ERROR))
        return
      }
      throw error
    }
  }

  forward() {
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.scannedBarcodes)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('pairing_read_result'),
        operation: PAIRING,
        extParams: JSON.stringify(this.extParams),
      },
    })
  }

  unmounted() {
    if (isKeyenceEnterpriseBrowser() && KeyenceModule && KeyenceModule.Scanner) {
      KeyenceModule.Scanner.clearReadCallback()
      KeyenceModule.Scanner.setConfig(ConfigId.CodeType, this.backupCodeTypeSetting)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.step-label {
  margin-bottom: 25px;
}

::v-deep .pair-scan-scanned-list-modal {
  .el-dialog__footer {
    width: 100%;
    padding: 8px 4px 12px;
    box-sizing: border-box;
    text-align: stretch;
  }

  .pair-scan-scanned-list-dialog-footer {
    display: block;
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .pair-scan-scanned-list-ok-button.el-button {
    width: 100% !important;
    max-width: none;
    min-height: 64px;
    height: auto;
    margin: 0 !important;
    padding: 16px;
    box-sizing: border-box;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.3;
    position: relative;
    display: block !important;
  }

  .pair-scan-scanned-list-ok-label {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    pointer-events: none;
    line-height: 1.3;
    color: #ffffff;
    white-space: nowrap;
  }
}
</style>
